import { Clock } from "@/components/icons";
import { ES_WEEKDAYS, ES_MONTHS, parseDt, fmtChip, conflictsWith } from "./dateUtils";

/**
 * Shows the doctor's already-taken slots for the next ~45 days so the patient
 * can pick a free time when rescheduling. Highlights chips that conflict with
 * the currently selected new datetime.
 */
export default function BusySlotsPanel({ busy, loading, selectedDate, duration, onPick }) {
  if (loading) {
    return (
      <div className="mb-3 px-3 py-2 rounded-xl bg-black/[0.03] text-xs text-black/55 text-center">
        Cargando horarios ocupados…
      </div>
    );
  }
  if (!busy || busy.length === 0) {
    return (
      <div className="mb-3 px-3 py-2 rounded-xl bg-blue-50 border border-emerald-200 text-xs text-emerald-800 text-center font-bold">
        El doctor tiene la agenda libre los próximos 45 días.
      </div>
    );
  }

  // Group by date (yyyy-mm-dd)
  const groups = {};
  for (const b of busy) {
    const key = (b.date || "").slice(0, 10);
    if (!groups[key]) groups[key] = [];
    groups[key].push(b);
  }
  const dayKeys = Object.keys(groups).sort();
  const selectedDay = (selectedDate || "").slice(0, 10);

  return (
    <div
      data-testid="busy-slots-panel"
      className="mb-3 bg-white rounded-2xl p-3 border border-[#E5E7EB]"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-bold uppercase tracking-widest text-black/55">
          Horarios ocupados
        </div>
        <div className="text-[10px] text-black/45">
          ({busy.length} cita{busy.length !== 1 ? "s" : ""})
        </div>
      </div>
      <div className="max-h-44 overflow-y-auto space-y-1 pr-1">
        {dayKeys.map((day) => {
          const dayDate = parseDt(day + "T00:00");
          const dayLabel = dayDate
            ? `${ES_WEEKDAYS[dayDate.getDay()].toUpperCase()} ${dayDate.getDate()} ${ES_MONTHS[dayDate.getMonth()]}`
            : day;
          const isSelectedDay = day === selectedDay;
          return (
            <div
              key={day}
              className={`rounded-xl px-2 py-1.5 ${
                isSelectedDay ? "bg-amber-50 border border-amber-200" : "bg-[#F5F5F5]"
              }`}
            >
              <div className="text-[10px] font-bold text-black/55 mb-1">{dayLabel}</div>
              <div className="flex flex-wrap gap-1">
                {groups[day].map((b) => {
                  const isConflict =
                    selectedDate && conflictsWith(selectedDate, duration, [b]) !== null;
                  return (
                    <span
                      key={b.id}
                      data-testid={`busy-chip-${b.id}`}
                      className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-bold ${
                        isConflict
                          ? "bg-red-100 text-red-800 border border-red-200"
                          : "bg-white text-black/65 border border-[#E5E7EB]"
                      }`}
                      title="Horario ocupado"
                    >
                      <Clock className="w-2.5 h-2.5" />
                      {fmtChip(b.date)} · {b.duration_min}m
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 text-[10px] text-black/50">
        Tu cita actual dura <strong>{duration} min</strong>. Evita que tu nuevo
        horario solape con los marcados.
      </div>
    </div>
  );
}
